import {onMounted, watch, type Ref} from "vue";
import {useResizeObserver, useEventListener} from "@vueuse/core";

/**
 * How far from the bottom the thread still counts as followed.
 *
 * Smooth scrolling lands a pixel or two short of the end, and a scene that
 * grows while the previous scroll is still running leaves a gap of its own:
 * without this tolerance the thread would stop following itself mid-course.
 */
const BOTTOM_THRESHOLD_PX = 120;

/**
 * How much of what came before stays in view above content that lands
 * taller than the viewport — enough for the Copilote line announcing it.
 */
const LANDING_CONTEXT_PX = 96;

/**
 * Where the thread lands when it changes step: which step, and whether it is
 * over — a record is read from its top, a conversation joined at its end.
 */
export interface CourseLanding {
    stepKey: Ref<string | null>;
    frozen: Ref<boolean>;
}

/**
 * Keep a growing course in view, the way a conversation follows itself.
 *
 * The thread grows at two rhythms — a scene added when a block completes, and,
 * inside a scene, the bubble, the card and the echo landing on their own
 * beats. Watching the played scenes would only catch the first; what is
 * observed here is the height of the content itself, so every appearance
 * scrolls, whichever rhythm it came from.
 *
 * Following stops as soon as the pharmacist scrolls up: the thread is also the
 * record of the interview, and re-reading it must not be snatched away by a
 * scene animating below. Coming back to the bottom resumes the following.
 *
 * Content landing taller than the viewport — a long list of treatments — is
 * not followed to its end, where the pharmacist would land lost: the thread
 * stops on its top instead, and stops following until they reach the bottom
 * again.
 */
export const useCourseAutoScroll = (
    viewport: Ref<HTMLElement | null>,
    content: Ref<HTMLElement | null>,
    landing: CourseLanding
) => {
    /**
     * Whether the thread is still following its own end.
     *
     * Held as state rather than measured when the content grows: a card lands
     * at its full height at once, so by the time the growth is observed the
     * end is already far below and any measurement taken then would read as
     * "scrolled up". What answers "was the pharmacist at the bottom" is the
     * position before the growth — which is to say, the one left by the last
     * scroll they made.
     */
    let following = true;

    /**
     * The height of the course as last seen, to tell how much a growth adds.
     */
    let height = 0;

    const measure = () => {
        height = viewport.value?.scrollHeight ?? 0;
    };

    const distanceToBottom = (element: HTMLElement) => {
        return element.scrollHeight - element.scrollTop - element.clientHeight;
    };

    const scrollToBottom = (behavior: ScrollBehavior) => {
        const element = viewport.value;

        if (element === null) {
            return;
        }

        element.scrollTo({top: element.scrollHeight, behavior});
    };

    /**
     * The pharmacist scrolling is the only thing that changes whether the thread
     * is followed — leaving the bottom stops it, coming back resumes it.
     *
     * The scrolls this composable performs land here too, at the bottom by
     * construction, so they keep the following on rather than cancelling it.
     */
    useEventListener(viewport, 'scroll', () => {
        const element = viewport.value;

        if (element === null) {
            return;
        }

        following = distanceToBottom(element) <= BOTTOM_THRESHOLD_PX;
    });

    useResizeObserver(content, () => {
        const element = viewport.value;

        if (element === null) {
            return;
        }

        const previous = height;

        measure();

        if (!following) {
            return;
        }

        if (previous > 0 && height - previous > element.clientHeight) {
            following = false;
            element.scrollTo({top: Math.max(0, previous - LANDING_CONTEXT_PX), behavior: 'smooth'});

            return;
        }

        scrollToBottom('smooth');
    });

    /**
     * The course opens at its end.
     *
     * Session resume lands on a course already played, and the viewport
     * only exists once the cycle is served — hence the watch rather than a
     * plain mount hook, which would run while it is still null.
     */
    onMounted(() => {
        measure();
        scrollToBottom('auto');
    });

    watch(viewport, element => {
        if (element !== null) {
            measure();
            scrollToBottom('auto');
        }
    }, {flush: 'post'});

    /**
     * Changing step lands the thread anew: a step over is a record, read from
     * its top; the one being worked on is a conversation, joined at its end.
     *
     * The swap replaces the content, which the resize observer sees as a
     * growth: the following is settled before scrolling so the observer does
     * not pull a record back down to its bottom, and the height is taken
     * afresh so the swap does not read as content landing.
     */
    watch(landing.stepKey, () => {
        measure();

        if (landing.frozen.value) {
            following = false;
            viewport.value?.scrollTo({top: 0, behavior: 'auto'});

            return;
        }

        following = true;
        scrollToBottom('auto');
    }, {flush: 'post'});
};
